import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daio1ejoh653ivfvoh80";

const tipTypes = [
  "原始材料 / 更早来源",
  "时间线节点补充",
  "来源标注有误，需要更正",
  "发现新的证据矛盾",
  "其他补充说明",
];

type Status = "idle" | "submitting" | "success" | "error";

export default function TipForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [detail, setDetail] = useState("");

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const tipType = String(formData.get("tip_type") ?? "").trim();
    const detailValue = String(formData.get("detail") ?? "").trim();

    if (!name) {
      setStatus("error");
      setFormError("请填写你的称呼，方便我们在需要补充时联系你。");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请填写有效的邮箱地址。");
      return;
    }
    if (!tipType) {
      setStatus("error");
      setFormError("请选择线索类型。");
      return;
    }
    if (!detailValue) {
      setStatus("error");
      setFormError("请简要说明你提供的线索内容。");
      return;
    }
    if (detailValue.length > 500) {
      setStatus("error");
      setFormError("线索说明请控制在 500 字以内。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string" && value.trim() !== "") {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        setDetail("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && serverMsg.toLowerCase() !== "ok"
            ? `提交未成功：${serverMsg}`
            : "提交未成功，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请检查网络后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="flex flex-wrap gap-4">
        <div className="min-w-[200px] flex-1">
          <label
            htmlFor="tip-name"
            className="block text-xs font-medium text-foreground-700"
          >
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="tip-name"
            name="name"
            type="text"
            required
            placeholder="如何称呼你"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div className="min-w-[200px] flex-1">
          <label
            htmlFor="tip-email"
            className="block text-xs font-medium text-foreground-700"
          >
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="tip-email"
            name="email"
            type="email"
            required
            placeholder="用于后续核对"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-4">
        <div className="min-w-[200px] flex-1">
          <label
            htmlFor="tip-type"
            className="block text-xs font-medium text-foreground-700"
          >
            线索类型 <span className="text-primary-600">*</span>
          </label>
          <select
            id="tip-type"
            name="tip_type"
            required
            defaultValue=""
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          >
            <option value="" disabled>
              请选择类型
            </option>
            {tipTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[200px] flex-1">
          <label
            htmlFor="tip-source"
            className="block text-xs font-medium text-foreground-700"
          >
            来源链接（选填）
          </label>
          <input
            id="tip-source"
            name="source_link"
            type="url"
            placeholder="https://"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label
          htmlFor="tip-detail"
          className="block text-xs font-medium text-foreground-700"
        >
          线索说明 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="tip-detail"
          name="detail"
          required
          maxLength={500}
          rows={5}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          placeholder="请描述你掌握的线索，尽量注明时间、出处和它的可核对方式（不超过 500 字）"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <p className="mt-1.5 text-right text-xs text-foreground-400">
          {detail.length} / 500
        </p>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-extra-verify-field"
      />

      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          我们只把你的联系方式用于核对线索，不会公开。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i
              className={
                status === "submitting"
                  ? "ri-loader-4-line animate-spin text-base"
                  : "ri-send-plane-line text-base"
              }
            ></i>
          </span>
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-checkbox-circle-line text-base"></i>
          </span>
          已收到你的线索，我们会尽快核对并与现有条目对照。
        </p>
      )}
      {status === "error" && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line text-base"></i>
          </span>
          {formError}
        </p>
      )}
    </form>
  );
}